import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTACopy } from 'shared'

/**
 * Parse `**bold**` markdown into React nodes. Keeps the rest of the text as-is.
 */
function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}

const ctaIcons: Record<string, React.ReactNode> = {
  phone: <Phone className="w-3 h-3" />,
  email: <Mail className="w-3 h-3" />,
}

function CopyButton({
  cta,
  label,
  copiedLabel,
}: {
  cta: ContactCTACopy
  label: string
  copiedLabel: string
}) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(cta.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] transition-colors',
        copied
          ? 'text-emerald-600'
          : 'text-[#4E2118]/50 hover:text-[#4E2118] hover:bg-[#4E2118]/5'
      )}
    >
      {copied ? <Check className="w-3 h-3" /> : ctaIcons[cta.id]}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content)
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] px-4 py-2.5',
          isUser
            ? 'bg-[#4E2118] text-white'
            : 'bg-white text-[#4E2118] shadow-sm border border-[#4E2118]/8'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-1">
            <span className="w-2 h-2 bg-[#DF631E]/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-[#DF631E]/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-[#DF631E]/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs — only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-1.5 ml-1">
          {contactCTAs.map((cta) =>
            cta.kind === 'copy' ? (
              <CopyButton
                key={cta.id}
                cta={cta}
                label={t(cta.labelKey)}
                copiedLabel={t('chatbot.cta.copied')}
              />
            ) : (
              <a
                key={cta.id}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] text-[#4E2118]/50 hover:text-[#4E2118] hover:bg-[#4E2118]/5 transition-colors"
              >
                {cta.icon && <img src={cta.icon} alt="" className="w-3 h-3" />}
                <span>{t(cta.labelKey)}</span>
              </a>
            )
          )}
        </div>
      )}
    </div>
  )
}
